import { useState } from "react";
import { toast } from "sonner";
import { Flag, ShieldAlert, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const FLAG_STYLE = {
  watch: ["bg-amber-50 text-amber-700 border-amber-200", "Sotto osservazione", Eye],
  blocked: ["bg-rose-50 text-rose-700 border-rose-200", "Bloccata", ShieldAlert],
};

export const FlagBadge = ({ flag, testId }) => {
  if (!flag?.status) return null;
  const [cls, label, Icon] = FLAG_STYLE[flag.status];
  return <span data-testid={testId} title={flag.note} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}><Icon className="h-3 w-3" />{label}</span>;
};

export function FlagButton({ customerId, flag, onChange, size = "sm", variant = "ghost" }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(flag?.status || "watch");
  const [note, setNote] = useState(flag?.note || "");
  const [busy, setBusy] = useState(false);

  const save = async (remove) => {
    setBusy(true);
    try {
      if (remove) { await api.delete(`/cards/flags/${customerId}`); onChange?.(null); toast.success("Segnalazione rimossa"); }
      else { const r = await api.put(`/cards/flags/${customerId}`, { status, note }); onChange?.(r.data); toast.success("Carta aggiornata"); }
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant={variant} data-testid={`flag-button-${customerId}`} className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); setStatus(flag?.status || "watch"); setNote(flag?.note || ""); setOpen(true); }}>
        <Flag className="mr-1 h-3 w-3" />{flag?.status ? "Modifica" : "Segnala"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="flag-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader><DialogTitle>Stato della carta nel CRM</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            {Object.entries(FLAG_STYLE).map(([k, [cls, label, Icon]]) => (
              <button type="button" key={k} data-testid={`flag-status-${k}`} onClick={() => setStatus(k)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg border p-3 text-sm transition-colors ${status === k ? cls + " ring-2 ring-offset-1 ring-slate-300" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                <Icon className="h-4 w-4" />{label}</button>))}
          </div>
          <Textarea data-testid="flag-note-input" placeholder="Nota per gli operatori (motivo, verifiche fatte...)" value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          <p className="text-xs text-slate-500">La segnalazione è solo interna al CRM: non modifica la carta sul sistema fedeltà.</p>
          <DialogFooter className="gap-2">
            {flag?.status && <Button variant="ghost" className="text-rose-600" data-testid="flag-remove" disabled={busy} onClick={() => save(true)}>Rimuovi</Button>}
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="flag-save" className="bg-blue-600 hover:bg-blue-700" disabled={busy} onClick={() => save(false)}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
